import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { colors } from '../../../theme';
import { homePageContent as content, LeadSegment } from '../content/home-page';

// ─── Blob SVG shapes ────────────────────────────────────────────────────────

/** Large yellow organic blob that sits behind the hero photo */
const YellowBlob = () => (
  <svg
    viewBox="0 0 400 400"
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-58%, -48%)',
      width: '115%',
      height: '115%',
      zIndex: 0,
    }}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill={colors.warning.main}
      d="M320,290Q280,330,230,355Q180,380,130,350Q80,320,55,270Q30,220,40,165Q50,110,90,75Q130,40,185,30Q240,20,285,55Q330,90,345,145Q360,200,340,250Q320,260,320,290Z"
    />
  </svg>
);

/** Small colored blob peeking out from the side of a card */
const CardBlob = ({ color, side }: { color: string; side: 'left' | 'right' }) => (
  <svg
    viewBox="0 0 100 180"
    style={{
      position: 'absolute',
      top: '50%',
      [side]: -2,
      transform: 'translateY(-50%)',
      width: 50,
      height: 110,
      zIndex: 0,
      opacity: 0.85,
    }}
    xmlns="http://www.w3.org/2000/svg"
  >
    {side === 'left' ? (
      <path
        fill={color}
        d="M50,0Q80,25,85,65Q90,105,65,140Q40,175,15,155Q-10,135,5,90Q20,45,30,15Z"
      />
    ) : (
      <path
        fill={color}
        d="M50,0Q20,25,15,65Q10,105,35,140Q60,175,85,155Q110,135,95,90Q80,45,70,15Z"
      />
    )}
  </svg>
);

// ─── Presentation ────────────────────────────────────────────────────────────
//
// Colour only. The words live in content/home-page.ts (PRT-01-FR-03); these
// pair with its entries by position.

const FEATURE_COLORS = [
  colors.secondary.main,
  colors.warning.dark,
  colors.green.main,
  colors.error.main,
];

const CARD_STYLES = [
  { color: colors.green.light, blobColor: colors.green.light, blobSide: 'left' as const },
  { color: colors.error.main, blobColor: colors.error.light, blobSide: 'left' as const },
  { color: colors.secondary.main, blobColor: colors.secondary.light, blobSide: 'right' as const },
];

const EMPHASIS_COLOR: Record<NonNullable<LeadSegment['emphasis']>, string> = {
  accent: colors.accent,
  error: colors.error.main,
};

const FEATURES = content.features.map((f, i) => ({
  ...f,
  bg: FEATURE_COLORS[i % FEATURE_COLORS.length],
}));

const CARDS = content.value.cards.map((c, i) => ({ ...c, ...CARD_STYLES[i % CARD_STYLES.length] }));

// ─── Component ───────────────────────────────────────────────────────────────

const HomePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* ══════════════════════════════════════════════════════════════════
          SECTION 1 — HERO
      ══════════════════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: 'white', py: { xs: 5, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* ── Left: circular photo + yellow blob ── */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: { xs: 260, md: 380 },
                }}
              >
                <YellowBlob />

                {/* Circular image */}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: { xs: 210, md: 300 },
                    height: { xs: 210, md: 300 },
                    borderRadius: '50%',
                    overflow: 'hidden',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    bgcolor: '#c8dff5',
                    border: '5px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '5rem', md: '7rem' },
                    userSelect: 'none',
                  }}
                >
                  👧🧒
                </Box>
              </Box>
            </Grid>

            {/* ── Right: copy ── */}
            <Grid item xs={12} md={7}>
              {/* Heading */}
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 900,
                  color: colors.primary.main,
                  lineHeight: 1.15,
                  mb: 1.5,
                  fontSize: { xs: '1.9rem', sm: '2.3rem', md: '2.7rem' },
                }}
              >
                {content.hero.titleLines.map((line, i) => (
                  <React.Fragment key={line}>
                    {i > 0 && <br />}
                    {line}
                  </React.Fragment>
                ))}
              </Typography>

              {/* Red squiggle underline */}
              <Box sx={{ mb: 2.5 }}>
                <svg width="130" height="14" viewBox="0 0 130 14" fill="none">
                  <path
                    d="M2 7 Q18 2 34 7 Q50 12 66 7 Q82 2 98 7 Q112 11 128 7"
                    stroke="#e53935"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </Box>

              {/* Lead text */}
              <Typography
                variant="body1"
                sx={{ color: colors.text.secondary, mb: 2.5, lineHeight: 1.75 }}
              >
                {content.hero.lead.map((segment, i) =>
                  segment.emphasis ? (
                    <Box
                      key={i}
                      component="span"
                      sx={{ color: EMPHASIS_COLOR[segment.emphasis], fontWeight: 700 }}
                    >
                      {segment.text}
                    </Box>
                  ) : (
                    <React.Fragment key={i}>{segment.text}</React.Fragment>
                  ),
                )}
              </Typography>

              {/* Checkmarks */}
              <Box sx={{ mb: 3.5 }}>
                {content.hero.checkmarks.map((item) => (
                  <Box
                    key={item}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.2 }}
                  >
                    <Box
                      sx={{
                        flexShrink: 0,
                        mt: '2px',
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        bgcolor: colors.green.light,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 900,
                        fontSize: '0.7rem',
                      }}
                    >
                      ✓
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text.secondary, lineHeight: 1.65 }}
                    >
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Primary CTA */}
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/courses')}
                sx={{
                  bgcolor: colors.primary.main,
                  color: 'white',
                  fontWeight: 700,
                  borderRadius: 10,
                  px: 4,
                  py: 1.4,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(74,144,226,0.4)',
                  '&:hover': {
                    bgcolor: colors.primary.dark,
                    boxShadow: '0 6px 26px rgba(74,144,226,0.5)',
                  },
                }}
              >
                {content.hero.ctaLabel}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 2 — YELLOW CTA BANNER
      ══════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          bgcolor: colors.warning.main,
          py: { xs: 3.5, md: 4.5 },
          textAlign: 'center',
          px: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: colors.navy,
            mb: 2.5,
            fontSize: { xs: '1rem', md: '1.1rem' },
          }}
        >
          {content.banner.text}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/courses')}
          sx={{
            bgcolor: colors.accent,
            color: 'white',
            fontWeight: 700,
            borderRadius: 10,
            px: 4.5,
            py: 1.3,
            fontSize: '0.95rem',
            textTransform: 'none',
            boxShadow: '0 4px 16px rgba(245,166,35,0.4)',
            '&:hover': { bgcolor: '#d6850c', boxShadow: '0 6px 20px rgba(245,166,35,0.5)' },
          }}
        >
          {content.banner.ctaLabel}
        </Button>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 3 — FEATURES ROW
      ══════════════════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: '#eef0fa', py: { xs: 3, md: 4 } }}>
        <Container maxWidth="lg">
          <Grid container justifyContent="center" spacing={{ xs: 2, md: 4 }}>
            {FEATURES.map((f) => (
              <Grid item xs={6} sm={3} key={f.label}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    justifyContent: { xs: 'flex-start', md: 'center' },
                  }}
                >
                  {/* Colored icon box */}
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      bgcolor: f.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      flexShrink: 0,
                    }}
                  >
                    {f.emoji}
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      color: colors.primary.main,
                      fontSize: '0.82rem',
                      lineHeight: 1.3,
                    }}
                  >
                    {f.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 4 — VALUE PROP / THREE CARDS
      ══════════════════════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: 'white', py: { xs: 6, md: 9 } }}>
        <Container maxWidth="lg">
          {/* Heading */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 900,
              color: colors.primary.main,
              textAlign: 'center',
              mb: 1.5,
              fontSize: { xs: '1.7rem', sm: '2rem', md: '2.3rem' },
            }}
          >
            {content.value.title}
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              color: colors.text.secondary,
              maxWidth: 560,
              mx: 'auto',
              mb: 6,
              lineHeight: 1.75,
            }}
          >
            {content.value.body}
          </Typography>

          {/* Cards */}
          <Grid container spacing={3} justifyContent="center">
            {CARDS.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <Box
                  sx={{
                    position: 'relative',
                    bgcolor: 'white',
                    borderRadius: 4,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                    p: { xs: 3.5, md: 4 },
                    overflow: 'hidden',
                    minHeight: 230,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 2,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 36px rgba(0,0,0,0.13)',
                    },
                  }}
                >
                  <CardBlob color={card.blobColor} side={card.blobSide} />

                  {/* Emoji */}
                  <Box sx={{ fontSize: '2.6rem', zIndex: 1, lineHeight: 1 }}>{card.emoji}</Box>

                  {/* Title */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 800, color: card.color, zIndex: 1, lineHeight: 1.3 }}
                  >
                    {card.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, zIndex: 1, lineHeight: 1.65 }}
                  >
                    {card.body}
                  </Typography>

                  {/* Checkmark badge */}
                  <Box
                    sx={{
                      mt: 'auto',
                      zIndex: 1,
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      bgcolor: card.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontWeight: 900,
                      fontSize: '1.1rem',
                    }}
                  >
                    ✓
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
